import React, { Component } from "react";
import * as XLSX from "xlsx";

const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api`;

const BLOOD_GROUPS = [
  "A+",
  "A-",
  "B+",
  "B-",
  "AB+",
  "AB-",
  "O+",
  "O-",
  "Unknown",
];
const GENDERS = ["Male", "Female", "Other"];
const LOCATIONS = ["Blood Camp", "Blood Bank", "Both"];
const STATUS_OPTIONS = ["approved", "rejected"];

const EMPTY_FORM = {
  id: null,
  fullName: "",
  fatherHusbandName: "",
  dob: "",
  age: "",
  gender: "",
  bloodGroup: "",
  weight: "",
  cnic: "",
  whatsapp: "",
  email: "",
  address: "",
  city: "",
  availableDays: "",
  timeSlot: "",
  emergencyName: "",
  emergencyRelation: "",
  emergencyPhone: "",
  password: "",
  confirm_password: "",
};

// ── Brand palette (matches dashboard / CSR management) ─────────
const BRAND = {
  red: "#dc3545",
  ink: "#1B1B1F",
  muted: "#6c757d",
  bg: "#f8f9fa",
  border: "#eef0f2",
};

class StatCard extends Component {
  render() {
    const { icon, value, label, tint } = this.props;
    return (
      <div className="col-md-4">
        <div
          className="d-flex align-items-center gap-3 p-3 h-100"
          style={{
            background: "#fff",
            border: `1px solid ${BRAND.border}`,
            borderRadius: 14,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: 46,
              height: 46,
              borderRadius: 12,
              background: tint,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 20 }}>{icon}</span>
          </div>
          <div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 700,
                color: BRAND.ink,
                lineHeight: 1.1,
              }}
            >
              {value}
            </div>
            <div className="text-muted small">{label}</div>
          </div>
        </div>
      </div>
    );
  }
}

class DonorManagement extends Component {
  constructor(props) {
    super(props);
    this.state = {
      donors: [],
      loading: true,
      error: null,

      showFormModal: false,
      formMode: "add",
      form: { ...EMPTY_FORM },
      formErrors: {},
      saving: false,

      showHistoryModal: false,
      historyDonor: null,
      historyLoading: false,
      historyEntries: [],

      search: "",
      statusFilter: "all",

      // ── Import state ──────────────────────────────────────────
      cities: [],
      showImportModal: false,
      importFile: null,
      importing: false,
      importProgress: { done: 0, total: 0 },
      importResults: [],

      // ── Status change state (single + bulk) ────────────────────
      showStatusModal: false,
      statusDonor: null,
      selectedStatus: "",
      updatingStatus: false,

      selectedRows: [],
      showBulkActions: false,
      showBulkStatusModal: false,
      bulkSelectedStatus: "",
      bulkUpdating: false,
      bulkProgress: { done: 0, total: 0 },
      bulkResults: [],
    };
  }

  componentDidMount() {
    this.fetchDonors();
    fetch(`${API_BASE}/cities`)
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          this.setState({ cities: data.data });
        }
      })
      .catch((err) => console.error("Failed to load cities:", err));
  }

  fetchDonors = () => {
    this.setState({ loading: true, error: null });
    fetch(`${API_BASE}/donors`)
      .then((res) => {
        if (!res.ok)
          throw new Error(`Failed to fetch donors (HTTP ${res.status}).`);
        return res.json();
      })
      .then((data) => {
        this.setState({
          donors: data.data || data.donors || [],
          loading: false,
        });
      })
      .catch((err) => {
        this.setState({ error: err.message, loading: false });
      });
  };

  fetchHistory = (donorId) => {
    this.setState({ historyLoading: true });
    fetch(`${API_BASE}/donors/${donorId}/history`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch history.");
        return res.json();
      })
      .then((data) => {
        const historyData = data.data || data.history || [];
        this.setState({
          historyEntries: historyData,
          historyLoading: false,
        });
      })
      .catch((error) => {
        console.error("Error fetching history:", error);
        this.setState({ historyEntries: [], historyLoading: false });
      });
  };

  openAddModal = () => {
    this.setState({
      showFormModal: true,
      formMode: "add",
      form: { ...EMPTY_FORM },
      formErrors: {},
    });
  };

  openEditModal = (donor) => {
    this.setState({
      showFormModal: true,
      formMode: "edit",
      formErrors: {},
      form: {
        id: donor.id,
        fullName: donor.full_name || "",
        fatherHusbandName: donor.father_husband_name || "",
        dob: donor.dob || "",
        age: donor.age || "",
        gender: donor.gender || "",
        bloodGroup: donor.blood_group || "",
        weight: donor.weight || "",
        cnic: donor.cnic || "",
        whatsapp: donor.whatsapp || "",
        email: donor.email || "",
        address: donor.address || "",
        city: donor.city || "",
        availableDays: donor.available_days || "",
        timeSlot: donor.time_slot || "",
        emergencyName: donor.emergency_name || "",
        emergencyRelation: donor.emergency_relation || "",
        emergencyPhone: donor.emergency_phone || "",
        password: "",
        confirm_password: "",
      },
    });
  };

  closeFormModal = () => {
    this.setState({
      showFormModal: false,
      form: { ...EMPTY_FORM },
      formErrors: {},
    });
  };

  openHistoryModal = (donor) => {
    this.setState({
      showHistoryModal: true,
      historyDonor: donor,
      historyEntries: [],
    });
    this.fetchHistory(donor.id);
  };

  closeHistoryModal = () => {
    this.setState({
      showHistoryModal: false,
      historyDonor: null,
      historyEntries: [],
    });
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({ form: { ...prev.form, [name]: value } }));
  };

  validateForm = () => {
    const { form, formMode } = this.state;
    const errors = {};

    if (!form.fullName) errors.fullName = "Full name is required.";
    if (!form.fatherHusbandName)
      errors.fatherHusbandName = "Father/husband name is required.";
    if (!form.dob && !form.age)
      errors.dob = "Date of birth or age is required.";
    if (!form.gender) errors.gender = "Gender is required.";
    if (!form.bloodGroup) errors.bloodGroup = "Blood group is required.";
    if (!form.weight) {
      errors.weight = "Weight is required.";
    } else if (form.weight < 45 || form.weight > 160) {
      errors.weight = "Weight must be between 45-160 kg.";
    }
    if (!form.cnic || form.cnic.replace(/\D/g, "").length !== 13) {
      errors.cnic = "CNIC must be 13 digits.";
    }
    if (!form.whatsapp) errors.whatsapp = "WhatsApp number is required.";
    if (!form.address) errors.address = "Address is required.";
    if (!form.city) errors.city = "City is required.";

    if (formMode === "add") {
      if (!form.email) errors.email = "Email is required.";
      if (!form.password) errors.password = "Password is required.";
      if (form.password && form.password.length < 6)
        errors.password = "Password must be at least 6 characters.";
      if (form.password !== form.confirm_password)
        errors.confirm_password = "Passwords do not match.";
    }

    this.setState({ formErrors: errors });
    return Object.keys(errors).length === 0;
  };

  buildFormData = () => {
    const { form } = this.state;
    const fd = new FormData();
    Object.keys(form).forEach((key) => {
      if (key === "id") return;
      if (form[key] !== null && form[key] !== undefined)
        fd.append(key, form[key]);
    });
    return fd;
  };

  handleSubmit = (e) => {
    e.preventDefault();
    if (!this.validateForm()) return;

    this.setState({ saving: true });
    const { formMode, form } = this.state;
    // POST is used for both create and edit because PHP doesn't reliably
    // parse multipart/form-data on PUT requests (needed for photo upload).
    const url =
      formMode === "add"
        ? `${API_BASE}/donors/register`
        : `${API_BASE}/donors/${form.id}`;

    fetch(url, { method: "POST", body: this.buildFormData() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        this.setState({ saving: false });
        if (!ok || data.status === false) {
          this.setState({
            formErrors: data.errors || {
              general: data.message || "Request failed.",
            },
          });
          return;
        }
        this.closeFormModal();
        this.fetchDonors();
      })
      .catch(() => {
        this.setState({
          saving: false,
          formErrors: {
            general: "Something went wrong. Check your connection.",
          },
        });
      });
  };

  filteredDonors = () => {
    const { donors, search, statusFilter } = this.state;
    return donors.filter((d) => {
      const matchesSearch =
        !search ||
        (d.full_name || "").toLowerCase().includes(search.toLowerCase()) ||
        (d.cnic || "").includes(search) ||
        (d.whatsapp || "").includes(search);
      const matchesStatus = statusFilter === "all" || d.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  initials = (name) =>
    (name || "?")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0].toUpperCase())
      .join("");

  renderField(label, name, value, error, type = "text", extra = {}) {
    return (
      <div className={extra.col || "col-md-6"}>
        <label className="form-label small fw-semibold">{label}</label>
        <input
          type={type}
          name={name}
          className={`form-control ${error ? "is-invalid" : ""}`}
          value={value}
          onChange={this.handleInputChange}
          {...extra.props}
        />
        {error && <div className="invalid-feedback">{error}</div>}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // ── Single-donor status change (same as DonorList.js) ──────────
  // ══════════════════════════════════════════════════════════════

  openStatusModal = (donor) => {
    this.setState({
      showStatusModal: true,
      statusDonor: donor,
      selectedStatus: "approved",
      updatingStatus: false,
    });
  };

  closeStatusModal = () => {
    this.setState({
      showStatusModal: false,
      statusDonor: null,
      selectedStatus: "",
      updatingStatus: false,
    });
  };

  handleStatusChange = (e) => {
    this.setState({ selectedStatus: e.target.value });
  };

  submitStatusUpdate = () => {
    const { statusDonor, selectedStatus } = this.state;
    if (!statusDonor || !selectedStatus) {
      return;
    }
    this.setState({ updatingStatus: true });
    fetch(`${API_BASE}/donors/${statusDonor.id}/status`, {
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ status: selectedStatus }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (data.status === true) {
          const updatedDonor = { ...statusDonor, status: selectedStatus };
          this.setState((prev) => ({
            donors: prev.donors.map((d) =>
              d.id === statusDonor.id ? updatedDonor : d,
            ),
          }));
          this.closeStatusModal();
        } else {
          this.setState({
            error: data.message || "Failed to update status.",
            updatingStatus: false,
          });
        }
      })
      .catch((err) => {
        console.error("Status update error:", err);
        this.setState({
          error: "Failed to update status. Please try again.",
          updatingStatus: false,
        });
      });
  };

  renderStatusModal() {
    const { showStatusModal, statusDonor, selectedStatus, updatingStatus } =
      this.state;
    if (!showStatusModal || !statusDonor) return null;

    return (
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title">Change Donor Status</h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={this.closeStatusModal}
                disabled={updatingStatus}
              />
            </div>
            <div className="modal-body">
              <div className="mb-4">
                <p className="mb-1">
                  <strong>Donor:</strong> {statusDonor.full_name}
                </p>
                <p className="mb-3">
                  <strong>Current Status:</strong>{" "}
                  {this.renderStatusBadge(statusDonor.status)}
                </p>
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">New Status</label>
                <select
                  className="form-select"
                  value={selectedStatus}
                  onChange={this.handleStatusChange}
                  disabled={updatingStatus}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light"
                onClick={this.closeStatusModal}
                disabled={updatingStatus}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={this.submitStatusUpdate}
                disabled={
                  updatingStatus || selectedStatus === statusDonor.status
                }
              >
                {updatingStatus ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Updating...
                  </>
                ) : (
                  "Update Status"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // ── Bulk status change (same as DonorList.js) ──────────────────
  // ══════════════════════════════════════════════════════════════

  toggleRowSelection = (donorId) => {
    this.setState((prev) => {
      const selectedRows = prev.selectedRows.includes(donorId)
        ? prev.selectedRows.filter((id) => id !== donorId)
        : [...prev.selectedRows, donorId];
      return {
        selectedRows,
        showBulkActions: selectedRows.length > 0,
      };
    });
  };

  selectAllRows = () => {
    const filtered = this.filteredDonors();
    const allIds = filtered.map((d) => d.id);
    const { selectedRows } = this.state;
    const allSelected = allIds.every((id) => selectedRows.includes(id));
    this.setState({
      selectedRows: allSelected ? [] : allIds,
      showBulkActions: !allSelected && allIds.length > 0,
    });
  };

  openBulkStatusModal = () => {
    this.setState({
      showBulkStatusModal: true,
      bulkSelectedStatus: "approved",
      bulkResults: [],
    });
  };

  closeBulkStatusModal = () => {
    this.setState({
      showBulkStatusModal: false,
      bulkSelectedStatus: "",
      bulkResults: [],
      bulkUpdating: false,
    });
  };

  handleBulkStatusChange = (e) => {
    this.setState({ bulkSelectedStatus: e.target.value });
  };

  submitBulkStatusUpdate = async () => {
    const { selectedRows, bulkSelectedStatus, donors } = this.state;
    if (!selectedRows.length || !bulkSelectedStatus) return;

    this.setState({
      bulkUpdating: true,
      bulkResults: [],
      bulkProgress: { done: 0, total: selectedRows.length },
    });

    const results = [];

    for (let i = 0; i < selectedRows.length; i++) {
      const id = selectedRows[i];
      const donor = donors.find((d) => d.id === id);
      const name = donor?.full_name || `Donor #${id}`;

      try {
        const res = await fetch(`${API_BASE}/donors/${id}/status`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({ status: bulkSelectedStatus }),
        });
        const data = await res.json();

        if (res.ok && data.status) {
          results.push({
            id,
            name,
            status: "success",
            message: `Set to ${bulkSelectedStatus}`,
          });
        } else {
          results.push({
            id,
            name,
            status: "error",
            message: data.message || "Failed.",
          });
        }
      } catch (err) {
        results.push({
          id,
          name,
          status: "error",
          message: "Network error: " + err.message,
        });
      }

      this.setState((prev) => ({
        bulkProgress: { ...prev.bulkProgress, done: i + 1 },
      }));
    }

    this.setState({
      bulkUpdating: false,
      bulkResults: results,
      selectedRows: [],
      showBulkActions: false,
    });
    this.fetchDonors();
  };

  renderBulkStatusModal() {
    const {
      showBulkStatusModal,
      selectedRows,
      bulkSelectedStatus,
      bulkUpdating,
      bulkProgress,
      bulkResults,
      donors,
    } = this.state;
    if (!showBulkStatusModal) return null;

    const selectedDonors = donors.filter((d) => selectedRows.includes(d.id));
    const successCount = bulkResults.filter(
      (r) => r.status === "success",
    ).length;
    const errorCount = bulkResults.filter((r) => r.status === "error").length;

    return (
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title">
                Change Status for {selectedRows.length} Donor(s)
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={this.closeBulkStatusModal}
                disabled={bulkUpdating}
              />
            </div>
            <div className="modal-body">
              {!bulkUpdating && bulkResults.length === 0 && (
                <>
                  <div
                    className="mb-3"
                    style={{ maxHeight: "180px", overflowY: "auto" }}
                  >
                    <ul className="list-group list-group-flush">
                      {selectedDonors.map((d) => (
                        <li
                          key={d.id}
                          className="list-group-item d-flex justify-content-between py-1 px-0"
                        >
                          <span>{d.full_name}</span>
                          {this.renderStatusBadge(d.status)}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      New Status for All Selected
                    </label>
                    <select
                      className="form-select"
                      value={bulkSelectedStatus}
                      onChange={this.handleBulkStatusChange}
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {bulkUpdating && (
                <div>
                  <div className="progress" style={{ height: "20px" }}>
                    <div
                      className="progress-bar bg-danger"
                      style={{
                        width: `${(bulkProgress.done / bulkProgress.total) * 100}%`,
                      }}
                    >
                      {bulkProgress.done}/{bulkProgress.total}
                    </div>
                  </div>
                  <p className="text-muted small mt-2 mb-0">
                    Updating donor by donor, please wait...
                  </p>
                </div>
              )}

              {!bulkUpdating && bulkResults.length > 0 && (
                <>
                  <div className="alert alert-secondary mb-2">
                    {successCount} updated successfully, {errorCount} failed.
                  </div>
                  <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkResults.map((r, i) => (
                          <tr
                            key={i}
                            className={
                              r.status === "error"
                                ? "table-danger"
                                : "table-success"
                            }
                          >
                            <td>{r.name}</td>
                            <td>{r.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light"
                onClick={this.closeBulkStatusModal}
                disabled={bulkUpdating}
              >
                Close
              </button>
              {bulkResults.length === 0 && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={this.submitBulkStatusUpdate}
                  disabled={bulkUpdating || !selectedRows.length}
                >
                  {bulkUpdating ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Updating...
                    </>
                  ) : (
                    `Update ${selectedRows.length} Donor(s)`
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // ── Excel Import (same approach as DonorList.js) ───────────────
  // ══════════════════════════════════════════════════════════════

  parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file."));
      reader.readAsArrayBuffer(file);
    });
  };

  normalizeImportRow = (row) => {
    const map = {
      fullname: "fullName",
      "full name": "fullName",
      name: "fullName",
      fatherhusbandname: "fatherHusbandName",
      "father/husband name": "fatherHusbandName",
      "father husband name": "fatherHusbandName",
      guardian: "fatherHusbandName",
      dob: "dob",
      "date of birth": "dob",
      age: "age",
      gender: "gender",
      bloodgroup: "bloodGroup",
      "blood group": "bloodGroup",
      weight: "weight",
      cnic: "cnic",
      whatsapp: "whatsapp",
      email: "email",
      address: "address",
      city: "city",
      availabledays: "availableDays",
      "available days": "availableDays",
      timeslot: "timeSlot",
      "time slot": "timeSlot",
      emergencyname: "emergencyName",
      "emergency name": "emergencyName",
      "emergency contact": "emergencyName",
      emergencyrelation: "emergencyRelation",
      "emergency relation": "emergencyRelation",
      relation: "emergencyRelation",
      emergencyphone: "emergencyPhone",
      "emergency phone": "emergencyPhone",
    };

    const normalized = {};
    Object.keys(row).forEach((key) => {
      const cleanKey = key.trim().toLowerCase();
      const mappedKey = map[cleanKey];
      if (mappedKey) {
        normalized[mappedKey] = String(row[key] ?? "").trim();
      }
    });
    return normalized;
  };

  resolveCityId = (cityValue) => {
    if (!cityValue) return "";
    if (/^\d+$/.test(cityValue)) return cityValue;
    const match = this.state.cities.find(
      (c) => c.name.trim().toLowerCase() === cityValue.trim().toLowerCase(),
    );
    return match ? String(match.id) : "";
  };

  handleImportFileChange = (e) => {
    this.setState({ importFile: e.target.files[0] || null, importResults: [] });
  };

  openImportModal = () => {
    this.setState({
      showImportModal: true,
      importFile: null,
      importResults: [],
      importProgress: { done: 0, total: 0 },
    });
  };

  closeImportModal = () => {
    this.setState({
      showImportModal: false,
      importFile: null,
      importResults: [],
      importing: false,
    });
  };

  submitImport = async () => {
    const { importFile } = this.state;
    if (!importFile) return;

    this.setState({ importing: true, importResults: [] });

    let rawRows;
    try {
      rawRows = await this.parseExcelFile(importFile);
    } catch (err) {
      this.setState({
        importing: false,
        importResults: [
          {
            row: "-",
            name: "-",
            status: "error",
            message: "Could not read the file: " + err.message,
          },
        ],
      });
      return;
    }

    if (!rawRows.length) {
      this.setState({
        importing: false,
        importResults: [
          {
            row: "-",
            name: "-",
            status: "error",
            message: "The file has no data rows.",
          },
        ],
      });
      return;
    }

    this.setState({ importProgress: { done: 0, total: rawRows.length } });

    const results = [];

    for (let i = 0; i < rawRows.length; i++) {
      const lineNo = i + 2;
      const row = this.normalizeImportRow(rawRows[i]);
      const rowName = row.fullName || "(blank)";

      if (!row.fullName && !row.cnic && !row.whatsapp) {
        results.push({
          row: lineNo,
          name: rowName,
          status: "error",
          message: "Empty row, skipped.",
        });
        this.setState((prev) => ({
          importProgress: { ...prev.importProgress, done: i + 1 },
        }));
        continue;
      }

      const fd = new FormData();
      fd.append("fullName", row.fullName || "");
      fd.append("fatherHusbandName", row.fatherHusbandName || "");
      fd.append("dob", row.dob || "");
      fd.append("age", row.age || "");
      fd.append("gender", row.gender || "");
      fd.append("bloodGroup", row.bloodGroup || "");
      fd.append("weight", row.weight || "");
      fd.append("cnic", row.cnic || "");
      fd.append("whatsapp", row.whatsapp || "");
      fd.append("email", row.email || "");
      fd.append("address", row.address || "");
      fd.append("city", this.resolveCityId(row.city));
      fd.append("availableDays", row.availableDays || "");
      fd.append("timeSlot", row.timeSlot || "");
      fd.append("emergencyName", row.emergencyName || "");
      fd.append("emergencyRelation", row.emergencyRelation || "");
      fd.append("emergencyPhone", row.emergencyPhone || "");

      try {
        const res = await fetch(`${API_BASE}/donors/register`, {
          method: "POST",
          body: fd,
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });
        const data = await res.json();

        if (res.ok && data.status) {
          results.push({
            row: lineNo,
            name: rowName,
            status: "success",
            message: `Registered as ${data.data.donor_id}`,
          });
        } else {
          const errMsg = data.errors
            ? Object.values(data.errors).join(", ")
            : data.message || "Failed.";
          results.push({
            row: lineNo,
            name: rowName,
            status: "error",
            message: errMsg,
          });
        }
      } catch (err) {
        results.push({
          row: lineNo,
          name: rowName,
          status: "error",
          message: "Network error: " + err.message,
        });
      }

      this.setState((prev) => ({
        importProgress: { ...prev.importProgress, done: i + 1 },
      }));
    }

    this.setState({ importing: false, importResults: results });
    this.fetchDonors();
  };

  renderImportModal() {
    const {
      showImportModal,
      importFile,
      importing,
      importProgress,
      importResults,
    } = this.state;
    if (!showImportModal) return null;

    const successCount = importResults.filter(
      (r) => r.status === "success",
    ).length;
    const errorCount = importResults.filter((r) => r.status === "error").length;

    return (
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title">Import Donors from Excel</h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={this.closeImportModal}
                disabled={importing}
              />
            </div>
            <div className="modal-body">
              <p className="text-muted small mb-2">
                Columns (any order, case-insensitive):{" "}
                <strong>
                  Full Name, Father/Husband Name, DOB or Age, Gender, Blood
                  Group, Weight, CNIC (required), WhatsApp, Email, Address,
                  City, Available Days, Time Slot, Emergency Name, Emergency
                  Relation, Emergency Phone (optional)
                </strong>
                . Each row goes through the same validation as adding a donor
                manually.
              </p>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="form-control"
                onChange={this.handleImportFileChange}
                disabled={importing}
              />

              {importing && (
                <div className="mt-3">
                  <div className="progress" style={{ height: "20px" }}>
                    <div
                      className="progress-bar bg-danger"
                      style={{
                        width: `${(importProgress.done / importProgress.total) * 100}%`,
                      }}
                    >
                      {importProgress.done}/{importProgress.total}
                    </div>
                  </div>
                  <p className="text-muted small mt-2 mb-0">
                    Importing row by row, please don't close this window...
                  </p>
                </div>
              )}

              {!importing && importResults.length > 0 && (
                <>
                  <div className="alert alert-secondary mt-3 mb-2">
                    {successCount} imported successfully, {errorCount} failed.
                  </div>
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Name</th>
                          <th>Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResults.map((r, i) => (
                          <tr
                            key={i}
                            className={
                              r.status === "error"
                                ? "table-danger"
                                : "table-success"
                            }
                          >
                            <td>{r.row}</td>
                            <td>{r.name}</td>
                            <td>{r.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light"
                onClick={this.closeImportModal}
                disabled={importing}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={this.submitImport}
                disabled={!importFile || importing}
              >
                {importing ? "Importing..." : "Upload & Import"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderHistoryModal() {
    const { showHistoryModal, historyDonor, historyLoading, historyEntries } =
      this.state;
    if (!showHistoryModal) return null;

    return (
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-scrollable modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h5 className="modal-title">
                Donor History:{" "}
                <span className="fw-normal">{historyDonor?.full_name}</span>
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={this.closeHistoryModal}
              />
            </div>
            <div className="modal-body">
              <h6 className="fw-bold mb-3">ACTIVITY LOG</h6>
              {historyLoading && (
                <p className="text-muted">Loading history...</p>
              )}
              {!historyLoading && historyEntries.length === 0 && (
                <p className="text-muted">No history records found.</p>
              )}
              {!historyLoading && historyEntries.length > 0 && (
                <div className="list-group">
                  {historyEntries.map((entry, idx) => (
                    <div key={idx} className="list-group-item">
                      {this.formatHistoryEntry(entry)}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light"
                onClick={this.closeHistoryModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  formatHistoryEntry(entry) {
    const { table_name, action_type, changes, user_name, created_at } = entry;
    const date = new Date(created_at);
    const formattedDate = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const formattedTime = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    const fieldNames = {
      full_name: "Name",
      dob: "Date of Birth",
      age: "Age",
      gender: "Gender",
      blood_group: "Blood Group",
      cnic: "CNIC",
      whatsapp: "WhatsApp",
      address: "Address",
      city: "City",
      emergency_name: "Emergency Contact",
      emergency_relation: "Emergency Relation",
      emergency_phone: "Emergency Phone",
      status: "Status",
      handling_office: "Handling Office",
      area: "Area",
      contact_number: "Contact Number",
    };

    const wrapper = (content) => (
      <>
        <div className="text-muted small">
          {formattedDate}, {formattedTime}
        </div>
        {content}
      </>
    );

    // ── Blood donation history entries ─────────────────────────
     if (table_name === "blood_donations") {
      const data = changes?.data || {};
      if (action_type === "created") {
        return wrapper(
          <div>
            <strong>{user_name || "Admin"}</strong> recorded a{" "}
            <strong>blood donation</strong>
            {data.patient_name ? (
              <>
                {" "}
                for patient <strong>{data.patient_name}</strong>
              </>
            ) : null}
            {data.donation_date ? (
              <>
                {" "}
                on <strong>{data.donation_date}</strong>
              </>
            ) : null}
            .
          </div>,
        );
      }
      if (action_type === "updated" && changes?.changes) {
        return wrapper(
          Object.entries(changes.changes).map(([field, value]) => (
            <div key={field}>
              <strong>{user_name || "System"}</strong> updated donation{" "}
              <strong>{fieldNames[field] || field.replace(/_/g, " ")}</strong>{" "}
              from <strong>{value.old || "Empty"}</strong> to{" "}
              <strong>{value.new || "Empty"}</strong>.
            </div>
          )),
        );
      }
      if (action_type === "deleted") {
        return wrapper(
          <div>
            <strong>{user_name || "System"}</strong> deleted a blood donation
            record.
          </div>,
        );
      }
    }

    // ── Blood issue history entries ────────────────────────────
      if (table_name === "blood_issues") {
      const data = changes?.data || {};
      if (action_type === "created") {
        return wrapper(
          <div>
            <strong>{user_name || "Admin"}</strong> recorded a{" "}
            <strong>blood issue</strong>
            {data.recipient_name ? (
              <>
                {" "}
                to <strong>{data.recipient_name}</strong>
              </>
            ) : null}
            {data.issue_date ? (
              <>
                {" "}
                on <strong>{data.issue_date}</strong>
              </>
            ) : null}
            .
          </div>,
        );
      }
      if (action_type === "updated" && changes?.changes) {
        return wrapper(
          Object.entries(changes.changes).map(([field, value]) => (
            <div key={field}>
              <strong>{user_name || "System"}</strong> updated issue{" "}
              <strong>{fieldNames[field] || field.replace(/_/g, " ")}</strong>{" "}
              from <strong>{value.old || "Empty"}</strong> to{" "}
              <strong>{value.new || "Empty"}</strong>.
            </div>
          )),
        );
      }
      if (action_type === "deleted") {
        return wrapper(
          <div>
            <strong>{user_name || "System"}</strong> deleted a blood issue
            record.
          </div>,
        );
      }
    }

    // ── Default: donor profile history (unchanged) ─────────────
    return wrapper(
      <>
        {action_type === "created" && (
          <div>
            <strong>{user_name || "Admin"}</strong> registered donor.
          </div>
        )}
        {action_type === "deleted" && (
          <div>
            <strong>{user_name || "System"}</strong> deleted this donor.
          </div>
        )}
        {action_type === "status_changed" && changes?.status && (
          <div>
            <strong>{user_name || "Admin"}</strong> changed donor status from{" "}
            <strong>{changes.status.old}</strong> to{" "}
            <strong>{changes.status.new}</strong>.
          </div>
        )}
        {action_type === "updated" &&
          changes?.changes &&
          Object.entries(changes.changes).map(([field, value]) => (
            <div key={field}>
              <strong>{user_name || "System"}</strong> updated{" "}
              <strong>{fieldNames[field] || field.replace(/_/g, " ")}</strong>{" "}
              from <strong>{value.old || "Empty"}</strong> to{" "}
              <strong>{value.new || "Empty"}</strong>.
            </div>
          ))}
      </>,
    );
  }

  renderStatusBadge(status) {
    const bg =
      status === "approved"
        ? "#198754"
        : status === "pending"
          ? "#B5730A"
          : BRAND.red;
    return (
      <span
        className="badge text-capitalize"
        style={{
          background: bg,
          borderRadius: 20,
          padding: "5px 12px",
          fontWeight: 500,
        }}
      >
        {status}
      </span>
    );
  }

  renderActions(donor) {
    return (
      <div className="d-flex align-items-center gap-2">

        <button
          className="btn btn-outline-danger"
          title="Change Status"
          onClick={() => this.openStatusModal(donor)}
        >
          <i className="bi bi-clipboard2-check-fill"></i>
        </button>
        <button
          className="btn btn-outline-info"
          title="History"
          onClick={() => this.openHistoryModal(donor)}
        >
          <i className="bi bi-clock-history"></i>
        </button>
      </div>
    );
  }

  render() {
    const { loading, error, search, statusFilter, donors, selectedRows } =
      this.state;
    const filtered = this.filteredDonors();
    const counts = {
      total: donors.length,
      approved: donors.filter((d) => d.status === "approved").length,
      pending: donors.filter((d) => d.status === "pending").length,
    };

    const allSelected =
      filtered.length > 0 && filtered.every((d) => selectedRows.includes(d.id));
    const someSelected = filtered.some((d) => selectedRows.includes(d.id));

    return (
      <div
        style={{ background: BRAND.bg, minHeight: "100vh" }}
        className="pt-5 mt-4 pb-4"
      >
        <div className="container-fluid px-4 px-lg-5">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4 pt-3">
            <div>
              <h4 className="fw-bold mb-1">Donor Management</h4>
              <p className="text-muted small mb-0">
                View and manage registered blood donors.
              </p>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-danger"
                style={{ borderRadius: 10 }}
                onClick={this.openImportModal}
              >
                <i className="bi bi-file-earmark-excel me-2"></i>
                Import
              </button>
              <button
                className="btn btn-danger"
                style={{ borderRadius: 10 }}
                onClick={this.openAddModal}
              >
                <i className="bi bi-plus-circle me-2"></i>
                Add Donor
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger py-2 small">⚠️ {error}</div>
          )}

          {/* Stat cards */}
          <div className="row g-3 mb-4">
            <StatCard
              icon="🩸"
              value={counts.total}
              label="Total Donors"
              tint="#FBEAEA"
            />
            <StatCard
              icon="✅"
              value={counts.approved}
              label="Approved"
              tint="#E9F7EF"
            />
            <StatCard
              icon="⏳"
              value={counts.pending}
              label="Pending"
              tint="#FDF3E3"
            />
          </div>

          {/* Bulk actions bar */}
          {this.state.showBulkActions && (
            <div className="alert alert-secondary d-flex justify-content-between align-items-center mb-3">
              <div>
                <strong>{selectedRows.length}</strong> donor(s) selected
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-success"
                  onClick={this.openBulkStatusModal}
                >
                  <i className="bi bi-check-circle me-1"></i> Approve/Reject
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() =>
                    this.setState({ selectedRows: [], showBulkActions: false })
                  }
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div
              className="bg-white text-center text-muted py-5"
              style={{ borderRadius: 14, border: `1px solid ${BRAND.border}` }}
            >
              <div className="spinner-border text-danger mb-2" />
              <div>Loading donors...</div>
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="bg-white text-center text-muted py-5"
              style={{ borderRadius: 14, border: `1px solid ${BRAND.border}` }}
            >
              <i
                className="bi bi-people"
                style={{ fontSize: 40, color: "#ccc" }}
              />
              <p className="mt-2 mb-0">No donors found.</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div
                className="card border-0 d-none d-md-block"
                style={{
                  borderRadius: 14,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold mb-0">All Donors</h6>
                    <span
                      className="badge"
                      style={{
                        background: BRAND.red,
                        borderRadius: 20,
                        padding: "6px 12px",
                      }}
                    >
                      {filtered.length} shown
                    </span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead>
                        <tr
                          style={{ borderBottom: `2px solid ${BRAND.border}` }}
                        >
                          <th style={{ width: 40 }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={allSelected}
                              ref={(el) => {
                                if (el)
                                  el.indeterminate = someSelected && !allSelected;
                              }}
                              onChange={this.selectAllRows}
                            />
                          </th>
                          <th
                            className="text-muted small text-uppercase"
                            style={{ letterSpacing: "0.04em" }}
                          >
                            Donor
                          </th>
                          <th
                            className="text-muted small text-uppercase"
                            style={{ letterSpacing: "0.04em" }}
                          >
                            Blood Group
                          </th>
                          <th
                            className="text-muted small text-uppercase"
                            style={{ letterSpacing: "0.04em" }}
                          >
                            CNIC
                          </th>
                          <th
                            className="text-muted small text-uppercase"
                            style={{ letterSpacing: "0.04em" }}
                          >
                            WhatsApp
                          </th>
                          <th
                            className="text-muted small text-uppercase"
                            style={{ letterSpacing: "0.04em" }}
                          >
                            City
                          </th>
                          <th
                            className="text-muted small text-uppercase"
                            style={{ letterSpacing: "0.04em" }}
                          >
                            Status
                          </th>
                          <th
                            className="text-muted small text-uppercase"
                            style={{ letterSpacing: "0.04em" }}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((donor) => {
                          const isSelected = selectedRows.includes(donor.id);
                          return (
                            <tr
                              key={donor.id}
                              className={isSelected ? "table-active" : ""}
                              style={{
                                borderBottom: `1px solid ${BRAND.border}`,
                              }}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={isSelected}
                                  onChange={() =>
                                    this.toggleRowSelection(donor.id)
                                  }
                                />
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                                    style={{
                                      width: 36,
                                      height: 36,
                                      fontSize: "0.85rem",
                                      background: BRAND.ink,
                                    }}
                                  >
                                    {this.initials(donor.full_name)}
                                  </div>
                                  <span className="fw-semibold">
                                    {donor.full_name}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <span
                                  className="badge"
                                  style={{
                                    background: "#FBEAEA",
                                    color: BRAND.red,
                                    borderRadius: 20,
                                    padding: "5px 12px",
                                    fontWeight: 600,
                                  }}
                                >
                                  {donor.blood_group}
                                </span>
                              </td>
                              <td className="text-muted small">{donor.cnic}</td>
                              <td className="text-muted small">
                                {donor.whatsapp}
                              </td>
                              <td className="text-muted small">
                                {donor.city_name}
                              </td>
                              <td>{this.renderStatusBadge(donor.status)}</td>
                              <td>{this.renderActions(donor)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Mobile cards */}
              <div className="d-md-none">
                {filtered.map((donor) => {
                  const isSelected = selectedRows.includes(donor.id);
                  return (
                    <div
                      className="bg-white p-3 mb-3"
                      key={donor.id}
                      style={{
                        borderRadius: 14,
                        border: `1px solid ${BRAND.border}`,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={isSelected}
                            onChange={() => this.toggleRowSelection(donor.id)}
                          />
                          <div
                            className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                            style={{
                              width: 36,
                              height: 36,
                              fontSize: "0.85rem",
                              background: BRAND.ink,
                            }}
                          >
                            {this.initials(donor.full_name)}
                          </div>
                          <div>
                            <div className="fw-semibold">
                              {donor.full_name}
                            </div>
                            <div className="small text-muted">
                              {donor.city}
                            </div>
                          </div>
                        </div>
                        {this.renderStatusBadge(donor.status)}
                      </div>
                      <div className="d-flex flex-wrap gap-3 small text-muted mb-2">
                        <span>
                          <span
                            className="badge"
                            style={{
                              background: "#FBEAEA",
                              color: BRAND.red,
                              borderRadius: 20,
                              padding: "4px 10px",
                              fontWeight: 600,
                            }}
                          >
                            {donor.blood_group}
                          </span>
                        </span>
                        <span>CNIC: {donor.cnic}</span>
                        <span>WhatsApp: {donor.whatsapp}</span>
                      </div>
                      <div
                        className="d-flex gap-1 pt-2 mt-2"
                        style={{ borderTop: `1px solid ${BRAND.border}` }}
                      >
                        {this.renderActions(donor)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {this.renderHistoryModal()}
          {this.renderImportModal()}
          {this.renderStatusModal()}
          {this.renderBulkStatusModal()}
        </div>
      </div>
    );
  }
}

export default DonorManagement;